// Pré-preenche o formulário de um pedido com os dados de GET /auth/profile e monta o
// payload de PUT /pessoa/:id quando o usuário edita um campo pré-preenchido.
// Funções puras — nenhuma chamada de API aqui, exceto a resolução de CEP (única parte
// que precisa de rede, isolada em resolveEnderecoIds).
//
// Nem todo campo do form tem espelho na API: campos de regra de negócio do pedido
// (ex: enderecoEmSeuNome, tipoDocumentoIdentificacao) simplesmente não aparecem no
// FIELD_MAP e são ignorados. Campos que existem nos dois lados mas têm formato
// incompatível SEM resolvedor disponível (nacionalidade, seccionalOrigem — ID de
// catálogo no PUT, texto sem resolvedor no GET) só entram como leitura (prefill),
// nunca no PUT — mesmo princípio de "nunca inventar código de catálogo externo" já
// usado pros tipoDocumentoProcesso/tipoLancamentoId deste módulo.
// naturalidade e municipioTitulo são ID de catálogo também, mas têm resolvedor: são o
// mesmo catálogo de Município, resolvido por nome via addressService.searchCities
// (CAMPOS_MUNICIPIO_POR_NOME, abaixo).
// nomeCompleto e dataNascimento nem tentam ir no PUT por um motivo diferente: não
// existem na whitelist do endpoint (ver README da API de Cadastro/Login) — não há
// coluna exposta pra atualizar esses dois dados por aqui.

import { addressService } from '../../../address/services/addressService';
import { unformat } from '../../../../utils/formatters';
import { resolverMunicipioPorNome, resolverMunicipioComLabelPorNome } from './enderecoResolverService';
import { escolherRegistroCep, criteriosCepDoFormulario } from '../../../address/utils/cepRegistros';
import { removerCamposOcultos } from '../utils/validation';
import {
  ESTADO_CIVIL_TEXTO_PARA_CODIGO,
  SEXO_TEXTO_PARA_LETRA,
  ETNIA_TEXTO_PARA_CODIGO,
  DOADOR_ORGAOS_TEXTO_PARA_CODIGO,
  EXERCE_FUNCAO_PUBLICA_TEXTO_PARA_CODIGO,
  DEFICIENCIA_NAO_POSSUI_ID,
  converterDeficienciaTexto
} from '../../config/shared/catalogosPessoaFisica';

// Catálogos de código→texto (sentido GET /profile → formulário) só existem aqui, porque
// só este arquivo faz esse sentido — os catálogos texto→código (sentido formulário → API)
// vêm de catalogosPessoaFisica.js, compartilhados com dadosRequerimentoService.js.
const ESTADO_CIVIL_CODIGO_PARA_TEXTO = Object.fromEntries(
  Object.entries(ESTADO_CIVIL_TEXTO_PARA_CODIGO).map(([texto, codigo]) => [codigo, texto])
);

// Só M/F: o form é um radio Masculino/Feminino (SEXO_OPCOES). "N" (Não informado, o
// padrão do cadastro) fica sem pré-preenchimento pra pessoa escolher — senão viraria um
// valor que o radio não mostra e a regra do reservista não reconhece.
const SEXO_LETRA_PARA_TEXTO = { M: 'Masculino', F: 'Feminino' };

// ETNIA_TEXTO_PARA_CODIGO tem "preta"->2 além de "negra"->2 (2 chaves pro mesmo código) —
// na volta código→texto isso incorporaria só uma das duas arbitrariamente pela ordem do
// Object.fromEntries, então construímos igual ao padrão de ETNIA_CODIGO_PARA_TEXTO
// original: uma tabela própria, canônica, código→texto.
const ETNIA_CODIGO_PARA_TEXTO = { 0: 'NaoInformado', 1: 'Branca', 2: 'Negra', 3: 'Parda', 4: 'Indigena', 5: 'Amarela' };

// doadorOrgaos: o form só oferece radio SIM/NAO (2 opções) — o código 3 ("Não
// informado") não tem opção equivalente no form, então só é usado no sentido GET→form
// (fica sem seleção nenhuma marcada) e nunca aparece no sentido form→PUT.
const DOADOR_ORGAOS_CODIGO_PARA_TEXTO = { 1: 'SIM', 2: 'NAO' };

const EXERCE_FUNCAO_PUBLICA_CODIGO_PARA_TEXTO = { 1: 'SIM', 2: 'NAO' };

const soData = (isoOuNull) => (isoOuNull ? String(isoOuNull).slice(0, 10) : undefined);

const identidade = (v) => v;

// Campos de "Dados Cadastrais" (usuario.dadosCadastrais do /profile). `toPut` ausente
// = campo só de leitura (prefill), nunca enviado no PUT.
const FIELD_MAP = {
  // CPF/CNPJ é a chave de identidade da pessoa, não faz parte da whitelist do PUT (não
  // dá pra "editar" o próprio documento por aqui) — só prefill. Note que isso é só pro
  // campo `cpf` (documento da PRÓPRIA pessoa logada, usado na maioria dos pedidos). O
  // campo `cnpj` de `identificacaoSociedadeSection` (sociedadeSections.js) é o CNPJ da
  // SOCIEDADE (outra entidade, às vezes ainda inexistente nos pedidos de registro) —
  // pra PF nunca deve ser prefilado com o cpfCnpj da pessoa, por isso não entra aqui.
  // Exceção: quando quem está logado é a própria PJ, nome e CNPJ da sociedade vêm do
  // login — ver valoresDoCadastro (abaixo) e adaptarConfigAoPerfil.
  cpf: { fromProfile: (u) => u?.dadosCadastrais?.cpfCnpj },
  nomeCompleto: { fromProfile: (u) => u?.dadosCadastrais?.nomeCompleto },
  // Número de registro na OAB (usado nos 2 pedidos de Comissões) — vem de usuario.oab,
  // não de dadosCadastrais. É gerido pelo cadastro interno da OAB, não pelo PUT.
  numeroOAB: { fromProfile: (u) => u?.oab?.numeroOAB },
  nomePai: { fromProfile: (u) => u?.dadosCadastrais?.nomePai, toPut: { key: 'nomePai', convert: identidade } },
  nomeMae: { fromProfile: (u) => u?.dadosCadastrais?.nomeMae, toPut: { key: 'nomeMae', convert: identidade } },
  naturalidade: { fromProfile: (u) => u?.dadosCadastrais?.naturalidade },
  nacionalidade: { fromProfile: (u) => u?.dadosCadastrais?.nacionalidade },
  seccionalOrigem: { fromProfile: (u) => u?.dadosCadastrais?.seccionalOrigem },
  dataNascimento: { fromProfile: (u) => soData(u?.dadosCadastrais?.dataNascimento) },
  // `deficiencia` (GET) vem como texto do catálogo (ex: "Deficiência Visual"), mas
  // o form usa o select de ID (DEFICIENCIA_OPCOES) e o PUT espera esse mesmo ID —
  // resolvido por nome exato contra o catálogo (ver catalogosPessoaFisica.js).
  // Sem deficiência registrada, `deficiencia` vem vazio/null: assume "Não Possui".
  possuiDeficiencia: {
    fromProfile: (u) => {
      const desc = u?.dadosCadastrais?.deficiencia;
      if (!desc) return DEFICIENCIA_NAO_POSSUI_ID;
      return converterDeficienciaTexto(desc);
    },
    toPut: { key: 'deficiencia', convert: (v) => Number(v) }
  },
  municipioTitulo: { fromProfile: (u) => u?.dadosCadastrais?.municipioTitulo },

  estadoCivil: {
    fromProfile: (u) => ESTADO_CIVIL_CODIGO_PARA_TEXTO[Number(u?.dadosCadastrais?.estadoCivil)],
    toPut: { key: 'estadoCivil', convert: (v) => ESTADO_CIVIL_TEXTO_PARA_CODIGO[v] }
  },
  sexo: {
    fromProfile: (u) => SEXO_LETRA_PARA_TEXTO[u?.dadosCadastrais?.sexo],
    toPut: { key: 'sexo', convert: (v) => SEXO_TEXTO_PARA_LETRA[String(v || '').trim().toLowerCase()] }
  },

  documentoIdentidade: { fromProfile: (u) => u?.dadosCadastrais?.documentoIdentidade, toPut: { key: 'documentoIdentidade', convert: identidade } },
  orgaoEmissor: { fromProfile: (u) => u?.dadosCadastrais?.orgaoEmissor, toPut: { key: 'orgaoEmissor', convert: identidade } },
  dataEmissao: { fromProfile: (u) => soData(u?.dadosCadastrais?.dataEmissao), toPut: { key: 'dataEmissao', convert: identidade } },
  tituloEleitor: { fromProfile: (u) => u?.dadosCadastrais?.tituloEleitor, toPut: { key: 'tituloEleitor', convert: identidade } },
  zonaEleitoral: { fromProfile: (u) => u?.dadosCadastrais?.zonaEleitoral, toPut: { key: 'zonaEleitoral', convert: identidade } },
  secaoEleitoral: { fromProfile: (u) => u?.dadosCadastrais?.secaoEleitoral, toPut: { key: 'secaoEleitoral', convert: identidade } },
  orgaoFuncaoPublica: { fromProfile: (u) => u?.dadosCadastrais?.orgaoFuncaoPublica, toPut: { key: 'orgaoFuncaoPublica', convert: identidade } },
  cargoFuncaoPublica: { fromProfile: (u) => u?.dadosCadastrais?.cargoFuncaoPublica, toPut: { key: 'cargoFuncaoPublica', convert: identidade } },
  numeroInscricaoOrigem: { fromProfile: (u) => u?.dadosCadastrais?.numeroInscricaoOrigem, toPut: { key: 'numeroInscricaoOrigem', convert: identidade } },
  dataInscricaoOrigem: { fromProfile: (u) => soData(u?.dadosCadastrais?.dataInscricaoOrigem), toPut: { key: 'dataInscricaoOrigem', convert: identidade } },

  etnia: {
    fromProfile: (u) => ETNIA_CODIGO_PARA_TEXTO[Number(u?.dadosCadastrais?.etnia)],
    toPut: { key: 'etnia', convert: (v) => ETNIA_TEXTO_PARA_CODIGO[String(v || '').trim().toLowerCase().replace(/\s+/g, '')] }
  },
  doadorOrgaos: {
    fromProfile: (u) => DOADOR_ORGAOS_CODIGO_PARA_TEXTO[Number(u?.dadosCadastrais?.doadorOrgaos)],
    toPut: { key: 'doadorOrgaos', convert: (v) => DOADOR_ORGAOS_TEXTO_PARA_CODIGO[v] }
  },
  exerceFuncaoPublica: {
    fromProfile: (u) => EXERCE_FUNCAO_PUBLICA_CODIGO_PARA_TEXTO[Number(u?.dadosCadastrais?.exerceFuncaoPublica)],
    toPut: { key: 'exerceFuncaoPublica', convert: (v) => EXERCE_FUNCAO_PUBLICA_TEXTO_PARA_CODIGO[v] }
  },

  // permiteDivulgar/cadastrarNaAASP ainda não vêm em GET /auth/profile (só existem
  // no PUT hoje) — sem fromProfile, o campo só nasce vazio/desmarcado no form.
  // Flags "S"/"N" em
  // string na API — não aceita true/false (ver README da API de Cadastro-Login).
  concordaDivulgacaoListagens: {
    fromProfile: () => undefined,
    toPut: { key: 'permiteDivulgar', convert: (v) => (v ? 'S' : 'N') }
  },
  cadastrarNaAASP: {
    fromProfile: () => undefined,
    toPut: { key: 'cadastrarNaAASP', convert: (v) => (v ? 'S' : 'N') }
  }
};

const coletarCamposPorNome = (config) => {
  const campos = new Map();
  (config?.steps || []).forEach((step) =>
    (step.sections || []).forEach((section) =>
      (section.fields || []).forEach((field) => campos.set(field.name, field))
    )
  );
  return campos;
};

const coletarNomesDeCampos = (config) => new Set(coletarCamposPorNome(config).keys());

// Bloco de endereço: 2 dialetos de nome de campo convivem no módulo (ver
// config/shared/enderecoSections.js x config/shared/sociedadeSections.js). O dialeto é
// detectado pela presença de `ruaResidencial`/`estadoResidencial` (exclusivo do dialeto de
// Sociedade) no próprio config do pedido — nunca os dois dialetos juntos no mesmo pedido.
const valoresEndereco = (origem, prefixo, dialetoRua, nomesCampos) => {
  if (!origem) return {};

  const valores = {};
  const add = (campo, valor) => {
    if (nomesCampos.has(campo) && valor !== undefined && valor !== null) valores[campo] = valor;
  };

  add(dialetoRua ? `rua${prefixo}` : `logradouro${prefixo}`, origem.logradouro);
  add(dialetoRua ? `estado${prefixo}` : `uf${prefixo}`, origem.uf);
  if (!dialetoRua) {
    add(`pais${prefixo}`, origem.pais);
    add(`complemento${prefixo}`, origem.complemento);
    add(`telefone${prefixo}`, origem.telefoneFixo || origem.telefone);
  }
  add(`cep${prefixo}`, origem.cep);
  add(`numero${prefixo}`, origem.numero);
  add(`bairro${prefixo}`, origem.bairro);
  add(`municipio${prefixo}`, origem.municipio);
  add(`celular${prefixo}`, origem.celular);
  add(`email${prefixo}`, origem.email);

  return valores;
};

/**
 * Monta os valores iniciais do formulário a partir do `usuario` retornado por
 * GET /auth/profile, restrito aos nomes de campo que o `config` do pedido realmente usa.
 * O mesmo objeto retornado aqui serve depois como base de comparação em
 * buildPessoaUpdatePayload — quem chamar precisa guardá-lo (ex: num ref).
 */
export const buildInitialValues = (profileUsuario, config) => {
  if (!profileUsuario || !config) return {};

  const nomesCampos = coletarNomesDeCampos(config);
  const valores = {};

  Object.entries(FIELD_MAP).forEach(([campo, { fromProfile }]) => {
    if (!nomesCampos.has(campo)) return;
    const valor = fromProfile(profileUsuario);
    if (valor !== undefined && valor !== null) valores[campo] = valor;
  });

  const dialetoRua = nomesCampos.has('ruaResidencial') || nomesCampos.has('estadoResidencial');
  if (nomesCampos.has(dialetoRua ? 'ruaResidencial' : 'logradouroResidencial')) {
    Object.assign(valores, valoresEndereco(profileUsuario.enderecoResidencial, 'Residencial', dialetoRua, nomesCampos));
  }

  // Endereço profissional/comercial da PRÓPRIA pessoa logada só existe no dialeto
  // enderecoSections.js (`logradouroProfissional`: bloco opcional da PF ou o Endereço
  // Comercial de PJ). O bloco "Profissional" do dialeto de Sociedade (`ruaProfissional`)
  // é o endereço de outra PJ, a sociedade do pedido — nunca prefilado aqui.
  if (nomesCampos.has('logradouroProfissional')) {
    Object.assign(valores, valoresEndereco(profileUsuario.enderecoProfissional, 'Profissional', false, nomesCampos));
  }

  return valores;
};

/**
 * Valores dos campos que já chegam travados com o dado do login (`valorDoCadastro`,
 * hoje só Nome da Sociedade e CNPJ na versão PJ — ver adaptarConfigAoPerfil).
 * Síncrono, sem depender do /auth/profile; não vai pro PUT (não está no FIELD_MAP).
 */
export const valoresDoCadastro = (config) => {
  const valores = {};
  coletarCamposPorNome(config).forEach((field, nome) => {
    if (field.valorDoCadastro) valores[nome] = field.valorDoCadastro;
  });
  return valores;
};

// Campos de texto do form cujo valor é um nome de município e cujo PUT espera o ID
// desse mesmo catálogo — resolvidos por nome via addressService.searchCities, não por
// FIELD_MAP (a resolução é assíncrona, incompatível com o toPut.convert síncrono usado
// pros demais campos).
const CAMPOS_MUNICIPIO_POR_NOME = ['naturalidade', 'municipioTitulo'];

/**
 * Resolve naturalidade/municipioTitulo pra {ID, Label} quando o campo do pedido é
 * `municipioBusca` (autocomplete — ver fieldComponents.js). `buildInitialValues`
 * só devolve o NOME em texto (é o que o /profile guarda), mas o autocomplete precisa
 * do ID em `formData[campo]` e do rótulo de exibição em `formData[campo + 'Label']`
 * (mesmo padrão do cadastro). Fica de fora de `buildInitialValues` (que é síncrono)
 * porque a resolução depende de addressService.searchCities.
 *
 * Pedidos que ainda usam texto livre pra esses 2 campos (a maioria, ainda não migrada
 * pro autocomplete) não chamam isto — o valor de buildInitialValues já serve como está.
 *
 * Se o nome vindo do perfil não bater com exatamente 1 município (ambíguo ou não
 * encontrado), o campo fica vazio em vez de guardar um texto que não é um ID válido
 * (mesmo princípio de "nunca inventar" usado nos demais campos) — a pessoa busca de
 * novo manualmente.
 */
export const resolverMunicipioBuscaInicial = async (initialValues, config) => {
  const campos = coletarCamposPorNome(config);
  const patch = {};

  for (const nomeCampo of CAMPOS_MUNICIPIO_POR_NOME) {
    const field = campos.get(nomeCampo);
    if (!field || field.type !== 'municipioBusca') continue;

    const nome = initialValues[nomeCampo];
    if (!nome) continue;

    const resolvido = await resolverMunicipioComLabelPorNome(nome);
    patch[nomeCampo] = resolvido ? String(resolvido.id) : '';
    patch[`${nomeCampo}Label`] = resolvido ? resolvido.label : '';
  }

  return patch;
};

// Resolve município/UF/país/CEP (IDs exigidos pelo PUT) a partir do CEP em texto que está
// no formulário, reaproveitando o mesmo endpoint já usado no cadastro (addressService.getByCEP).
// CEP com vários registros: usa o escolhido na tela (`criterios`, ver
// criteriosCepDoFormulario); ambíguo não resolve (não chuta um registro).
const resolveEnderecoIds = async (cep, criterios) => {
  const cepLimpo = unformat(cep || '');
  if (cepLimpo.length !== 8) return null;

  try {
    const response = await addressService.getByCEP(cepLimpo);
    const dados = escolherRegistroCep(response?.data?.data || [], criterios);
    if (!dados) return null;

    return { cep: dados.id, municipio: dados.cidadeId, uf: dados.estadoId, pais: dados.paisId };
  } catch {
    return null; // CEP não encontrado ou erro de rede: não bloqueia o fluxo, só não resolve os IDs dessa vez
  }
};

const buildEnderecoPayload = async ({ formData, initialValues, prefixo, putSufixo, dialetoRua }) => {
  const payload = {};
  const mudou = (campo) => formData[campo] !== undefined && formData[campo] !== initialValues[campo];

  const logradouroCampo = dialetoRua ? `rua${prefixo}` : `logradouro${prefixo}`;
  if (mudou(logradouroCampo)) payload[`logradouro${putSufixo}`] = formData[logradouroCampo];

  ['numero', 'bairro', 'celular', 'email'].forEach((base) => {
    const campo = `${base}${prefixo}`;
    if (mudou(campo)) payload[`${base}${putSufixo}`] = formData[campo];
  });

  if (!dialetoRua) {
    ['complemento', 'telefone'].forEach((base) => {
      const campo = `${base}${prefixo}`;
      if (mudou(campo)) payload[`${base}${putSufixo}`] = formData[campo];
    });
  }

  const cepCampo = `cep${prefixo}`;
  if (mudou(cepCampo)) {
    const ids = await resolveEnderecoIds(formData[cepCampo], criteriosCepDoFormulario(formData, cepCampo));
    if (ids) {
      payload[`cep${putSufixo}`] = ids.cep;
      payload[`municipio${putSufixo}`] = ids.municipio;
      payload[`uf${putSufixo}`] = ids.uf;
      payload[`pais${putSufixo}`] = ids.pais;
    }
  }

  return payload;
};

/**
 * Compara `formData` contra os valores originais do /profile (`initialValues`, vindo de
 * buildInitialValues) e monta o payload pronto pra PUT /pessoa/:id — só com os campos que
 * mudaram e que têm conversão segura. Retorna `{}` se nada relevante mudou.
 */
export const buildPessoaUpdatePayload = async ({ config, formData: formDataCompleto, initialValues }) => {
  if (!config || !formDataCompleto || !initialValues) return {};

  // Campo escondido por showWhen (ex: endereço profissional depois do "Não") não
  // atualiza o cadastro — ficou de fora do pedido, então fica de fora do PUT também.
  const formData = removerCamposOcultos(formDataCompleto, config.steps);
  const campos = coletarCamposPorNome(config);
  const nomesCampos = new Set(campos.keys());
  const payload = {};

  Object.entries(FIELD_MAP).forEach(([campo, { toPut }]) => {
    if (!toPut || !nomesCampos.has(campo)) return;

    const valorAtual = formData[campo];
    if (valorAtual === undefined || valorAtual === initialValues[campo]) return;

    const valorConvertido = toPut.convert(valorAtual);
    if (valorConvertido === undefined || valorConvertido === null || Number.isNaN(valorConvertido)) return;

    payload[toPut.key] = valorConvertido;
  });

  const dialetoRua = nomesCampos.has('ruaResidencial') || nomesCampos.has('estadoResidencial');
  if (nomesCampos.has(dialetoRua ? 'ruaResidencial' : 'logradouroResidencial')) {
    Object.assign(payload, await buildEnderecoPayload({
      formData, initialValues, prefixo: 'Residencial', putSufixo: 'Residencial', dialetoRua
    }));
  }

  if (nomesCampos.has('logradouroProfissional')) {
    Object.assign(payload, await buildEnderecoPayload({
      formData, initialValues, prefixo: 'Profissional', putSufixo: 'Comercial', dialetoRua: false
    }));
  }

  for (const campo of CAMPOS_MUNICIPIO_POR_NOME) {
    const field = campos.get(campo);
    if (!field) continue;
    if (formData[campo] === undefined || formData[campo] === initialValues[campo]) continue;

    if (field.type === 'municipioBusca') {
      // Autocomplete: formData[campo] já é o ID do município (guardado pelo
      // SearchableSelect), sem precisar resolver por nome.
      payload[campo] = Number(formData[campo]);
    } else {
      const municipioId = await resolverMunicipioPorNome(formData[campo]);
      if (municipioId) payload[campo] = municipioId;
    }
  }

  return payload;
};
